import { oids } from "@/configs/Settings";
import {
  triggerAutomationMessaging,
  type TriggerAutomationMessagingResult,
} from "src-next/modules/crm-clinical/automation/services/automationMessagingTriggerService";
import {
  triggerBirthdayAutomationBatch,
  type TriggerBirthdayAutomationResult,
} from "src-next/modules/crm-clinical/automation/services/birthdayAutomationTriggerService";
import type { CrmPatientSummary } from "src-next/modules/crm-clinical/communications/types";
import { toast } from "react-toastify";
import {
  buildMessagingAppointment,
  buildMessagingPatient,
  buildPractitionerRoleRequesterReference,
  extractCpfFromPatientReference,
  extractPatientDisplayName,
  extractPatientPhone,
  extractResourceIdFromReference,
  logAutomationTriggerError,
  normalizeAutomationOrganizationId,
} from "src-next/modules/crm-clinical/automation/helpers/automationMessagingTriggerHelpers";
import type { MessagingPatient } from "src-next/modules/crm-clinical/messaging/types";

/**
 * Payload mínimo que os fluxos legados devem fornecer para disparo de automação.
 * Mantido desacoplado de React/FHIR para facilitar chamadas best-effort.
 */
export interface ClinicalAutomationTriggerInput {
  organizationId: string;
  practitionerId: string;
  requesterReference?: string | null;
  appointmentId: string | number;
  appointmentStartAtIso?: string | null;
  patient: MessagingPatient;
}

/** Contexto opcional para wrapper de PatchAppointmentStatus → booked. */
export interface PreConsultationAutomationTriggerContext {
  organizationId: string;
  professionalActorReference?: string | null;
  patientActorReference?: string | null;
  patientDisplayName?: string | null;
  patientCpf?: string | null;
  patientPhone?: string | null;
  appointmentStartAtIso?: string | null;
  requesterReference?: string | null;
}

export function buildPreConsultationTriggerContextFromAppointment(params: {
  organizationId?: string | null;
  appointment?: fhir5.Appointment | null;
  patientDisplayName?: string | null;
  patientCpf?: string | null;
  patientPhone?: string | null;
  requesterReference?: string | null;
}): PreConsultationAutomationTriggerContext | null {
  const organizationId = normalizeAutomationOrganizationId(
    params.organizationId,
  );
  const appointment = params.appointment;
  if (!organizationId || !appointment?.id) return null;

  const patientParticipant = appointment.participant?.find((participant) =>
    String(participant.actor?.reference ?? "").includes("Patient"),
  );
  const practitionerParticipant = appointment.participant?.find((participant) =>
    String(participant.actor?.reference ?? "").includes("Practitioner"),
  );

  return {
    organizationId,
    professionalActorReference: practitionerParticipant?.actor?.reference,
    patientActorReference: patientParticipant?.actor?.reference,
    patientDisplayName:
      params.patientDisplayName ??
      patientParticipant?.actor?.display ??
      "Paciente",
    patientCpf: params.patientCpf,
    patientPhone: params.patientPhone,
    appointmentStartAtIso: appointment.start,
    requesterReference: params.requesterReference,
  };
}

export function buildTriggerInputFromPreConsultationContext(
  appointmentId: string,
  context: PreConsultationAutomationTriggerContext,
): ClinicalAutomationTriggerInput | null {
  return buildClinicalAutomationTriggerInputFromAppointmentForm({
    organizationId: context.organizationId,
    professionalActorReference: context.professionalActorReference,
    patientActorReference: context.patientActorReference,
    patientDisplayName: context.patientDisplayName,
    patientCpf: context.patientCpf,
    patientPhone: context.patientPhone,
    appointmentId,
    appointmentStartAtIso: context.appointmentStartAtIso,
    requesterReference: context.requesterReference,
  });
}
export function buildClinicalAutomationTriggerInputFromAppointmentForm(params: {
  organizationId?: string | null;
  professionalActorReference?: string | null;
  patientActorReference?: string | null;
  patientDisplayName?: string | null;
  patientCpf?: string | null;
  patientPhone?: string | null;
  appointmentId: string | number;
  appointmentStartAtIso?: string | null;
  requesterReference?: string | null;
}): ClinicalAutomationTriggerInput | null {
  const organizationId = normalizeAutomationOrganizationId(
    params.organizationId,
  );
  const practitionerId = extractResourceIdFromReference(
    params.professionalActorReference,
  );
  const patient = buildMessagingPatient({
    fullName: params.patientDisplayName,
    cpf: params.patientCpf,
    phone: params.patientPhone,
    patientActorReference: params.patientActorReference,
  });

  if (!organizationId || !practitionerId || !patient) {
    return null;
  }

  return {
    organizationId,
    practitionerId,
    requesterReference: params.requesterReference,
    appointmentId: params.appointmentId,
    appointmentStartAtIso: params.appointmentStartAtIso,
    patient,
  };
}

/**
 * Monta input a partir de appointment FHIR + patient FHIR (Footer / agenda).
 */
export function buildClinicalAutomationTriggerInputFromFhirResources(params: {
  organizationId?: string | null;
  practitionerId?: string | null;
  requesterReference?: string | null;
  appointment?: fhir5.Appointment | null;
  patient?: fhir5.Patient | null;
  patientReference?: string | null;
}): ClinicalAutomationTriggerInput | null {
  const organizationId = normalizeAutomationOrganizationId(
    params.organizationId,
  );
  const practitionerId = String(params.practitionerId ?? "").trim();
  const appointmentId = String(params.appointment?.id ?? "").trim();

  if (!organizationId || !practitionerId || !appointmentId) {
    return null;
  }

  const patientReference =
    params.patientReference ??
    params.appointment?.participant?.find((participant) =>
      String(participant.actor?.reference ?? "").includes("Patient"),
    )?.actor?.reference;

  const patient = buildMessagingPatient({
    id: params.patient?.id ? String(params.patient.id) : undefined,
    fullName: extractPatientDisplayName(params.patient),
    cpf: extractCpfFromPatientReference(patientReference),
    phone: extractPatientPhone(params.patient),
    patientActorReference: patientReference,
  });

  if (!patient) return null;

  return {
    organizationId,
    practitionerId,
    requesterReference: params.requesterReference,
    appointmentId,
    appointmentStartAtIso: params.appointment?.start,
    patient,
  };
}

const AUTOMATION_USER_LABELS: Record<
  "triage" | "preConsultation" | "postConsultation",
  string
> = {
  triage: "triagem",
  preConsultation: "pré-consulta",
  postConsultation: "pós-consulta",
};

/** Skips esperados quando automação não está em uso — não incomodam o usuário. */
function isSilentAutomationSkip(reason: string): boolean {
  return /não configurada|desabilitada|sem template configurado|organizationId ausente|contexto de automação incompleto/i.test(
    reason,
  );
}

function isFranchiseAutomationSkip(reason: string): boolean {
  return /saldo de mensagens|mensagens crm/i.test(reason);
}

/**
 * Exibe toast nos fluxos internos (agenda clínica / atendimento).
 * Agenda externa (PractitionerCards) permanece silenciosa.
 */
function notifyInternalAutomationResult(
  automationId: "preConsultation" | "postConsultation",
  result: TriggerAutomationMessagingResult | null,
): void {
  if (!result || result.status === "success") return;

  const label = AUTOMATION_USER_LABELS[automationId];

  if (result.status === "skipped") {
    if (isSilentAutomationSkip(result.reason)) return;

    if (isFranchiseAutomationSkip(result.reason)) {
      toast.warning(
        `Não foi possível enviar a mensagem automática de ${label}: saldo de mensagens insuficiente. Contrate um pacote no CRM para continuar enviando.`,
      );
      return;
    }

    toast.warning(
      `Não foi possível enviar a mensagem automática de ${label}: ${result.reason}`,
    );
    return;
  }

  toast.error(
    `Falha ao enviar a mensagem automática de ${label}: ${result.errorMessage}`,
  );
}

async function runClinicalAutomationTrigger(
  automationId: "triage" | "preConsultation" | "postConsultation",
  input: ClinicalAutomationTriggerInput,
  options?: { notifyUser?: boolean },
): Promise<TriggerAutomationMessagingResult | null> {
  try {
    const result = await triggerAutomationMessaging({
      automationId,
      context: {
        organizationId: input.organizationId,
        practitionerId: input.practitionerId,
      },
      appointment: buildMessagingAppointment({
        appointmentId: input.appointmentId,
        startAtIso: input.appointmentStartAtIso,
      }),
      patient: input.patient,
      requesterReference: input.requesterReference,
    });

    if (
      options?.notifyUser &&
      (automationId === "preConsultation" ||
        automationId === "postConsultation")
    ) {
      notifyInternalAutomationResult(automationId, result);
    }

    return result;
  } catch (error) {
    logAutomationTriggerError(
      `Falha best-effort ao disparar automação "${automationId}".`,
      error,
    );

    if (
      options?.notifyUser &&
      (automationId === "preConsultation" ||
        automationId === "postConsultation")
    ) {
      toast.error(
        `Falha ao enviar a mensagem automática de ${AUTOMATION_USER_LABELS[automationId]}. Tente novamente.`,
      );
    }

    return null;
  }
}

/** Dispara automação de triagem (agenda externa — sem toast; caller deve aguardar). */
export async function triggerTriageAutomation(
  input: ClinicalAutomationTriggerInput,
): Promise<TriggerAutomationMessagingResult | null> {
  return runClinicalAutomationTrigger("triage", input, { notifyUser: false });
}

/** Dispara automação de pré-consulta (status booked). */
export async function triggerPreConsultationAutomation(
  input: ClinicalAutomationTriggerInput,
): Promise<TriggerAutomationMessagingResult | null> {
  return runClinicalAutomationTrigger("preConsultation", input, {
    notifyUser: true,
  });
}

/** Dispara automação de pós-consulta (finalizar atendimento). */
export async function triggerPostConsultationAutomation(
  input: ClinicalAutomationTriggerInput,
): Promise<TriggerAutomationMessagingResult | null> {
  return runClinicalAutomationTrigger("postConsultation", input, {
    notifyUser: true,
  });
}

export interface BirthdayAutomationTriggerInput {
  organizationId: string;
  practitionerId: string;
  requesterReference?: string | null;
  allPatients: CrmPatientSummary[];
}

/**
 * Dispara automação de aniversariantes em lote (dashboard profissional).
 * Best-effort com toast informativo em falhas relevantes.
 */
export async function triggerBirthdayAutomation(
  input: BirthdayAutomationTriggerInput,
): Promise<TriggerBirthdayAutomationResult | null> {
  try {
    const result = await triggerBirthdayAutomationBatch({
      context: {
        organizationId: input.organizationId,
        practitionerId: input.practitionerId,
      },
      requesterReference: input.requesterReference,
      allPatients: input.allPatients,
    });

    if (result.status === "success") {
      toast.success("Mensagens de aniversário enviadas com sucesso!");
      return result;
    }

    if (result.status === "skipped") {
      if (!isSilentAutomationSkip(result.reason)) {
        if (isFranchiseAutomationSkip(result.reason)) {
          toast.warning(
            "Não foi possível enviar as mensagens de aniversário: saldo de mensagens insuficiente. Contrate um pacote no CRM para continuar enviando.",
          );
        } else {
          toast.warning(
            `Não foi possível enviar as mensagens de aniversário: ${result.reason}`,
          );
        }
      }
      return result;
    }

    toast.error(
      `Falha ao enviar mensagens de aniversário: ${result.errorMessage}`,
    );
    return result;
  } catch (error) {
    logAutomationTriggerError(
      'Falha best-effort ao disparar automação "birthday".',
      error,
    );
    toast.error(
      "Falha ao enviar mensagens de aniversário. Tente novamente.",
    );
    return null;
  }
}

/**
 * Helper para montar requesterReference no padrão da agenda interna.
 */
export function buildScheduleManagerRequesterReference(params: {
  organizationId?: string | null;
  practitionerUserId?: string | null;
}): string {
  const orgId = normalizeAutomationOrganizationId(params.organizationId);
  const userId = String(params.practitionerUserId ?? "").trim();
  if (!orgId || !userId) return "";
  return buildPractitionerRoleRequesterReference({
    organizationId: orgId,
    practitionerUserId: `${oids.cpf}-${userId}`,
  });
}

/**
 * Extrai CPF de participant reference em appointments da agenda interna.
 */
export function extractPatientCpfFromAppointmentParticipants(
  participants?: fhir5.AppointmentParticipant[],
): string {
  const patientRef = participants?.find((participant) =>
    String(participant.actor?.reference ?? "").includes("Patient"),
  )?.actor?.reference;
  return extractCpfFromPatientReference(patientRef);
}

/**
 * Referências da agenda externa (PractitionerCards):
 * - Practitioner vem do Schedule.actor
 * - Organization vem de Location.managingOrganization
 * - PractitionerRole = `{organizationId}-{practitionerId}` (ids FHIR completos, com OID)
 */
export interface ExternalScheduleAutomationRefs {
  /** ID completo da Organization (ex.: `2.16.76.1.3.3-12345678901234`). */
  organizationFhirId: string;
  /** ID completo do Practitioner (ex.: `2.16.840.1.113883.13.237-77820061120`). */
  practitionerFhirId: string;
  organizationReference: string;
  practitionerReference: string;
  requesterReference: string;
}

export function buildExternalSchedulePractitionerRoleReference(params: {
  organizationFhirId: string;
  practitionerFhirId: string;
}): string {
  const organizationFhirId = String(params.organizationFhirId ?? "").trim();
  const practitionerFhirId = String(params.practitionerFhirId ?? "").trim();
  if (!organizationFhirId || !practitionerFhirId) return "";
  return `PractitionerRole/${organizationFhirId?.split("-")?.[1]}-${practitionerFhirId}`;
}

/**
 * Monta referências de automação/envio a partir do Schedule selecionado e da Location
 * associada (Organization via managingOrganization).
 */
export function buildExternalScheduleAutomationRefs(params: {
  schedule?: fhir5.Schedule | null;
  location?: fhir5.Location | null;
  /** Fallback quando Location não traz managingOrganization (ex.: org já resolvida no estado). */
  organizationFhirIdFallback?: string | null;
}): ExternalScheduleAutomationRefs | null {
  const schedule = params.schedule;
  if (!schedule) return null;

  const practitionerActor = schedule.actor?.find((actor) =>
    String(actor.reference ?? "").startsWith("Practitioner/"),
  );
  const practitionerReference = String(
    practitionerActor?.reference ?? "",
  ).trim();
  const practitionerFhirId = extractResourceIdFromReference(
    practitionerReference,
  );

  const organizationReferenceFromLocation = String(
    params.location?.managingOrganization?.reference ?? "",
  ).trim();
  const organizationFhirId =
    extractResourceIdFromReference(organizationReferenceFromLocation) ||
    String(params.organizationFhirIdFallback ?? "").trim();

  if (!practitionerFhirId || !organizationFhirId) {
    return null;
  }

  const organizationReference = organizationReferenceFromLocation.startsWith(
    "Organization/",
  )
    ? organizationReferenceFromLocation
    : `Organization/${organizationFhirId}`;

  const requesterReference = buildExternalSchedulePractitionerRoleReference({
    organizationFhirId,
    practitionerFhirId,
  });

  if (!requesterReference) return null;

  return {
    organizationFhirId,
    practitionerFhirId,
    organizationReference,
    practitionerReference,
    requesterReference,
  };
}
